// src/components/pages/ChangeEmailPage/ChangeEmailPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { useAppStore } from '../../store/useAppStore';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { apiService } from '../../services/api_url';
import Loader from '../../widgets/Loader/Loader';
import { BackIcon } from '../../assets/images/BackIcon';
import { CheckIcon, CloseIcon } from '../../assets/images/ReferralIcons';

import './ChangeEmailPage.scss';

const ChangeEmailPage: React.FC = () => {
    const navigate = useNavigate();
    const { telegramId, userData, loading: userLoading } = useUserData();
    const { updateUserEmail } = useAppStore();

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (userData?.email) {
            setEmail(userData.email);
        }
    }, [userData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const trimmedEmail = email.trim();

        // 1️⃣ Валидация email
        const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
        if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
            // Ошибка унифицирована с серверной
            setError('Введите корректный email');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${apiService.getBaseURL()}/user/change-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    telegram_id: telegramId,
                    new_email: trimmedEmail
                })
            });

            const data = await response.json();

            if (data.success) {
                updateUserEmail(trimmedEmail);

                setSuccess(true);
                setTimeout(() => navigate('/profile'), 2000);
            } else {
                // Любая ошибка сервера (например, email занят) выводится так же, как ошибка валидации
                setError(data.message || 'Ошибка при изменении email');
            }
        } catch (err) {
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    if (userLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="change-email-page loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    if (!telegramId) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="change-email-page">
                    <div className="error-container">
                        <div className="error-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Для изменения email нужно войти в систему</p>
                        <button className="continue-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="change-email-page">
                <div className="add-email-header">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <BackIcon />
                    </button>
                    <div className="add-email-title">Изменение email</div>
                </div>

                {error && (
                    <div className="error-message">
                        <CloseIcon />
                        <span>{error}</span>
                    </div>
                )}

                {success ? (
                    <div className="success-message detailed">
                        <div className="success-icon-large"><CheckIcon /></div>
                        <div className="success-content">
                            <div className="success-title">Email изменен!</div>
                            <div className="success-details">
                                <div className="detail-row">
                                    <span className="detail-label">Новый email:</span>
                                    <span className="detail-value">{email}</span>
                                </div>
                            </div>
                            <p className="success-note">Перенаправление в профиль...</p>
                        </div>
                    </div>
                ) : (
                    <div className="form-card">
                        <form onSubmit={handleSubmit} className="email-form">
                            <div className="form-group">
                                <label htmlFor="email">Новый email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="example@mail.com"
                                    disabled={loading}
                                    autoComplete="off"
                                />
                            </div>

                            <button type="submit" className="submit-button" disabled={loading}>
                                {loading ? 'Сохраняем...' : 'Сохранить'}
                            </button>
                        </form>

                        <div className="password-hint">
                            *Сменить пароль можно на сайте <a href="https://shadownet.nl" target="_blank" rel="noopener noreferrer">shadownet.nl</a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChangeEmailPage;